import { React, useState } from 'react';
import { useMsal } from "@azure/msal-react";
import ModalInformativo from '../../components/Modal'
import { Container, Table } from 'react-bootstrap'
import { FaSearch, FaPhone, FaUser, FaIdCard, FaStar } from 'react-icons/fa'
import apiService from '../../services/api'
import { handleAuthError } from '../../utils/authHelper'
import './numerificador.css'

const BASE_TITLE = process.env.REACT_APP_BASE_TITLE

export default function Numerificador() {
    const [clientData, setClientData] = useState()
    const [clientNumber, setClientNumber] = useState('')
    const [textModal, setTextModal] = useState()
    const [showModal, setShowModal] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [inputError, setInputError] = useState('')

    const { accounts, instance } = useMsal(); // Agregamos instance
    const { name } = accounts[0] ?? {}

    // Validación en tiempo real del número
    const validateNumber = (number) => {
        if (!number) {
            setInputError('')
            return false
        }
        
        // Debe empezar con 56 y tener 11 dígitos
        const regex = /^56[0-9]{9}$/
        if (!regex.test(number)) {
            setInputError('El formato debe ser: 56 + 9 dígitos (Ej: 56912345678)')
            return false
        }
        
        setInputError('')
        return true
    }

    const handleNumberChange = (e) => {
        const value = e.target.value.replace(/\D/g, '') // Solo números
        setClientNumber(value)
        if (value) validateNumber(value)
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const handleSearch = async () => {
        if (!clientNumber) {
            setShowModal(true)
            setTextModal("Por favor, ingrese un número de celular para realizar la búsqueda")
            return
        }

        if (!validateNumber(clientNumber)) {
            setShowModal(true)
            setTextModal("El formato del número es incorrecto. Debe ingresar 11 dígitos comenzando con 56 (Ej: 56912345678)")
            return
        }

        setIsLoading(true)
        setClientData(null)

        try {
            const response = await apiService.searchNumber({ numero: clientNumber });
            
            if (!response.res) {
                setShowModal(true)
                setTextModal("El número ingresado NO se encuentra en la base de datos. Por favor verifique el número e inténtelo nuevamente.")
            } else {
                setClientData(response.data)
            }
        } catch (error) {
            console.error("Error en búsqueda:", error.message)
            
            // Manejo de error de autenticación (token expirado)
            const authHandled = handleAuthError(error, instance);
            if (authHandled) {
                // Si es error de auth, el helper se encarga del logout/redirect
                return;
            }
            
            // Número no encontrado en la base de datos
            if (error.notFound) {
                setShowModal(true)
                setTextModal("El número ingresado NO se encuentra en la base de datos. Por favor verifique el número e inténtelo nuevamente.")
                return
            }

            // Otros errores
            setShowModal(true)
            setTextModal("Lo sentimos, estamos experimentando problemas técnicos. Por favor, inténtelo nuevamente en unos momentos.")
        } finally {
            setIsLoading(false)
        }
    }

    const getSegmentClass = (segment) => {
        if (!segment) return 'segment-default'
        const seg = segment.toLowerCase()
        if (seg.includes('prime')) return 'segment-prime'
        if (seg.includes('classic')) return 'segment-classic'
        return 'segment-default'
    }

    return (
        <div className="numerificador-container">
            <ModalInformativo
                onClose={() => setShowModal(false)}
                show={showModal}
                children={textModal}
            />
            
            <Container>
                {/* Header */}
                <div className="numerificador-header">
                    <h1 className="numerificador-title">{BASE_TITLE}</h1>
                    <p className="session-info">👤 Sesión activa: <strong>{name}</strong></p>
                    <p className="info-text">Consulta información de clientes ingresando su número telefónico</p>
                </div>

                {/* Buscador */}
                <div className="search-card">
                    <label className="search-label">
                        <FaPhone style={{ marginRight: '8px' }} />
                        Número de Celular
                    </label>
                    <div className="search-input-group">
                        <input
                            type="text"
                            className={`search-input ${inputError ? 'input-error' : ''}`}
                            placeholder="Ejemplo: 56912345678"
                            maxLength="11"
                            value={clientNumber}
                            onChange={handleNumberChange}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                        />
                        <button 
                            className="search-btn"
                            onClick={handleSearch}
                            disabled={isLoading || !!inputError}
                        >
                            {isLoading ? (
                                <>
                                    <div className="spinner"></div>
                                    Buscando...
                                </>
                            ) : (
                                <>
                                    <FaSearch />
                                    Buscar
                                </>
                            )}
                        </button>
                    </div>
                    {inputError && <span className="input-error-message">⚠️ {inputError}</span>}
                    {!inputError && clientNumber && <span className="input-hint">✓ Formato correcto</span>}
                    {!clientNumber && <span className="input-hint">💡 Ingrese 11 dígitos comenzando con 56</span>}
                </div>
                {/* Resultados */}
                {clientData && (
                    <div className="results-container">
                        <div className="results-card">
                            <h2 className="results-title">📋 Información del Cliente</h2>
                            
                            {/* Grid de información principal */}
                            <div className="info-grid">
                                <div className="info-item">
                                    <div className="info-label">
                                        <FaIdCard style={{ marginRight: '5px' }} />
                                        RUT
                                    </div>
                                    <div className="info-value">
                                        {clientData.rut || clientData.Rut || 'No disponible'}
                                    </div>
                                </div>

                                <div className="info-item">
                                    <div className="info-label">
                                        <FaUser style={{ marginRight: '5px' }} />
                                        Nombre
                                    </div>
                                    <div className="info-value">
                                        {clientData.nombre ? clientData.nombre.toUpperCase() : 'No disponible'}
                                    </div>
                                </div>

                                <div className="info-item">
                                    <div className="info-label">
                                        <FaPhone style={{ marginRight: '5px' }} />
                                        Teléfono
                                    </div>
                                    <div className="info-value">
                                        {clientData.celular || clientData.numero_cliente || 'No disponible'}
                                    </div>
                                </div>

                                {clientData.segmento && (
                                    <div className="info-item">
                                        <div className="info-label">
                                            <FaStar style={{ marginRight: '5px' }} />
                                            Segmento
                                        </div>
                                        <div className="info-value">
                                            <span className={`segment-badge ${getSegmentClass(clientData.segmento)}`}>
                                                {clientData.segmento.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {clientData.nroreq && (
                                    <div className="info-item">
                                        <div className="info-label">Nro. Requerimiento</div>
                                        <div className="info-value">{clientData.nroreq.toUpperCase()}</div>
                                    </div>
                                )}
                            </div>

                            {/* Campañas vigentes */}
                            {(clientData.camp_vigente_1 || clientData.camp_vigente_2 || clientData.camp_vigente_3) && (
                                <div className="info-item" style={{ marginTop: '1rem' }}>
                                    <div className="info-label">📢 Campañas de Fidelización</div>
                                    <button 
                                        className="campaign-btn"
                                        onClick={() => {
                                            setShowModal(true);
                                            setTextModal(
                                                <Table striped bordered hover className="data-table">
                                                    <thead>
                                                        <tr>
                                                            <th>DATO</th>
                                                            <th>INFORMACIÓN</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {clientData.camp_vigente_1 && (
                                                            <tr>
                                                                <td style={{ textAlign: "left" }}>CAMPAÑA VIGENTE NRO. 1</td>
                                                                <td>{clientData.camp_vigente_1.toUpperCase()}</td>
                                                            </tr>
                                                        )}
                                                        {clientData.camp_vigente_2 && (
                                                            <tr>
                                                                <td style={{ textAlign: "left" }}>CAMPAÑA VIGENTE NRO. 2</td>
                                                                <td>{clientData.camp_vigente_2.toUpperCase()}</td>
                                                            </tr>
                                                        )}
                                                        {clientData.camp_vigente_3 && (
                                                            <tr>
                                                                <td style={{ textAlign: "left" }}>CAMPAÑA VIGENTE NRO. 3</td>
                                                                <td>{clientData.camp_vigente_3.toUpperCase()}</td>
                                                            </tr>
                                                        )}
                                                        {clientData.script_sugerido_1 && (
                                                            <tr>
                                                                <td style={{ textAlign: "left" }}>SCRIPT SUGERIDO NRO. 1</td>
                                                                <td>{clientData.script_sugerido_1.toUpperCase()}</td>
                                                            </tr>
                                                        )}
                                                        {clientData.script_sugerido_2 && (
                                                            <tr>
                                                                <td style={{ textAlign: "left" }}>SCRIPT SUGERIDO NRO. 2</td>
                                                                <td>{clientData.script_sugerido_2.toUpperCase()}</td>
                                                            </tr>
                                                        )}
                                                        {clientData.script_sugerido_3 && (
                                                            <tr>
                                                                <td style={{ textAlign: "left" }}>SCRIPT SUGERIDO NRO. 3</td>
                                                                <td>{clientData.script_sugerido_3.toUpperCase()}</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            );
                                        }}
                                    >
                                        Ver Detalles de Campañas
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Container>
        </div>
    );
}